import type { DropdownOption } from 'components/DropdownBuilder';
import { EMPTY_ARRAY } from 'constants/defaults';
import { difference, intersection } from 'lodash-es';
import type { TagNormalized, TagsDictionary } from 'redux/modules/tags.types';
import type { Hour } from 'types/hour';
import type { ProjectLabel } from 'types/project';
import type { Tag } from 'types/tag';
import { naturalCompare } from 'utils/naturalSort';

/**
 * Turn tag array /w children into DropdownBuilder-compatible options
 */
export interface TagOption extends DropdownOption<number> {
  children?: Array<TagOption>;
  id: number;
  required: boolean;
  name: string;
  active: boolean;
  hasBeenRemoved?: boolean;
  image?: string;
  budget?: number | null;
  parent_id?: number | null;
  isSelected: boolean;
  isPartiallySelected?: boolean;
}

export function buildTagFilterOptions(
  allTags: Array<Tag>,
  selectedIds: Array<number>,
  requiredIds: Array<number> = EMPTY_ARRAY,
  excludeArchived?: boolean,
  defaultIds: Array<number> = EMPTY_ARRAY
): Array<TagOption> {
  const selectedSet = new Set(selectedIds);
  const requiredSet = new Set(requiredIds);

  let tags = allTags;
  if (excludeArchived || defaultIds.length) {
    tags = tags.filter(({ active, id, parent_id }) => {
      let shouldIncludeTag = !excludeArchived || (active === null ? true : active);

      if (defaultIds.length) {
        const isDefaultTag =
          defaultIds.includes(id) || (parent_id && defaultIds.includes(parent_id));

        shouldIncludeTag &&= !isDefaultTag;
      }

      return shouldIncludeTag;
    });
  }

  return tags
    .map((tag): TagOption => {
      const allChildren = tag.children || [];
      const children = excludeArchived
        ? allChildren.filter(({ active }) => (active == null ? true : active))
        : allChildren;

      const mappedChildren = children.map((child): TagOption => {
        return {
          sequence: child.sequence,
          value: child.id,
          label: child.name,
          name: child.name,
          image: child.emoji,
          active: child.active == null ? true : child.active,
          budget: child.budget,
          parent_id: tag.id,
          hasBeenRemoved: !!child.hasBeenRemoved,
          required: requiredSet.has(child.id),
          id: child.id,
          isSelected: selectedSet.has(child.id) || selectedSet.has(tag.id),
          searchValue: child.name + tag.name,
        };
      });
      const numChildTags = children.length;
      const parentTagName = numChildTags
        ? `${tag.name} (${numChildTags} sub-tag${numChildTags !== 1 ? 's' : ''})`
        : tag.name;

      const hasSelectedChildren =
        mappedChildren.length > 0 && mappedChildren.some(child => child.isSelected);
      const isPartiallySelected =
        hasSelectedChildren && !mappedChildren.every(child => child.isSelected);

      return {
        sequence: tag.sequence,
        value: tag.id,
        id: tag.id,
        label: tag.name,
        name: parentTagName,
        image: tag.emoji,
        active: tag.active == null ? true : tag.active,
        budget: tag.budget,
        hasBeenRemoved: !!tag.hasBeenRemoved,
        isSelected: selectedSet.has(tag.id) || hasSelectedChildren,
        isPartiallySelected,
        required: requiredSet.has(tag.id),
        children: mappedChildren,
        searchValue: tag.name,
      };
    })
    .sort((tagA: TagOption, tagB: TagOption) => {
      // both required, or both not required
      if (tagA.required === tagB.required) return naturalCompare(tagA.label, tagB.label);

      // if one of them is required, but the other isn't
      if (tagA.required && !tagB.required) return -1;
      if (!tagA.required && tagB.required) return 1;

      return 0;
    });
}

export function getSelectedWithActiveTagsOnProject(
  t: Tag,
  savedHour: Hour<unknown>,
  projectTags: Array<number>
): Tag | null {
  if (!savedHour) {
    return t;
  }
  let tag = t;
  const safeChildren = tag.children || [];
  const isOnProject = (projectTags ?? []).includes(tag.id);
  const removedFromProject =
    projectTags != null ? difference(savedHour.label_ids, projectTags) : [];
  const hasArchivedTags = !tag.active || safeChildren.some(s => !s.active);
  const projectTagsSet = new Set(projectTags);

  // if the tag is on the project and nothing has been removed or archived we can return
  if (isOnProject && !removedFromProject.length && !hasArchivedTags) {
    const children = safeChildren.filter(child => projectTagsSet.has(child.id));

    return { ...tag, children };
  }

  // are we interested in this tag? is it selected or on the project?
  const tagIdsOnProjectOrHour = intersection(
    [tag.id, ...safeChildren.map(c => c.id)],
    [...(projectTags ?? []), ...savedHour.label_ids]
  );

  const isParentArchivedAndNotSelected = !tag.active && !savedHour.label_ids.includes(tag.id);

  if (!tagIdsOnProjectOrHour.length || isParentArchivedAndNotSelected) {
    return null;
  }

  const parentHasBeenRemoved = removedFromProject.includes(tag.id);
  const childHasBeenRemoved = safeChildren.find(child => removedFromProject.includes(child.id));

  // check for removed parents or children, and add a key marking them
  if (parentHasBeenRemoved && !childHasBeenRemoved) {
    tag = {
      ...tag,
      children: [],
      hasBeenRemoved: true,
    };
  } else if (childHasBeenRemoved) {
    const childrenWithoutOriginal = safeChildren.filter(c => c.id !== childHasBeenRemoved.id);
    tag = {
      ...tag,
      children: [...childrenWithoutOriginal, { ...childHasBeenRemoved, hasBeenRemoved: true }],
    };
  }

  function filterRelevantTags(child: Tag) {
    const idHasBeenRemoved = removedFromProject.includes(child.id);
    const idIsOnProject = (projectTags ?? []).includes(child.id);
    const idIsActiveOrOnSavedHour = child.active || savedHour.label_ids.includes(child.id);
    return idHasBeenRemoved || (idIsOnProject && idIsActiveOrOnSavedHour);
  }

  if (removedFromProject.length) {
    return {
      ...tag,
      children: tag.children?.filter(filterRelevantTags),
    };
  }

  return {
    ...tag,
    children: tag.children?.filter(child => {
      if (child.active && (projectTags ?? []).includes(child.id)) {
        return true;
      }
      return !child.active && savedHour.label_ids.includes(child.id);
    }),
  };
}

export function getActiveTagsOnProject(tag: Tag, projectTags: Array<number>) {
  if (!tag.active) {
    return null;
  }

  const children = (tag.children || []).filter(child => projectTags.includes(child.id));

  if (children.length > 0 || projectTags.includes(tag.id)) {
    return { ...tag, children: children.filter(c => c.active) };
  }

  return null;
}

export function tagsToDictionary(tags: Array<Tag>): Record<number, Tag> {
  return tags.reduce((dictionary: Record<number, Tag>, tag) => {
    tag.children?.forEach(childTag => {
      dictionary[childTag.id] = childTag;
    });
    dictionary[tag.id] = tag;
    return dictionary;
  }, {});
}

export function mergeAndSortTagLookups(
  ids: Array<number>,
  tags: TagsDictionary,
  lookups: Record<number, ProjectLabel>
): Array<TagNormalized & ProjectLabel> {
  return ids
    .map(id => ({
      ...tags[id],
      ...lookups[id],
    }))
    .sort((a, b) => naturalCompare(a.sequence, b.sequence));
}
